import { useState } from "react";
import { TableCell } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import {
  Button,
  Loading,
  Modal,
  TextInput,
} from "src/Components";
import { IAdditional } from "src/Tables/TableTemplate/tableTemplate";
import { ILimitItemTableReport } from "src/Interfaces/portfolioGroups";
import {
  deletePortfoGroupLimitItem,
  editPortfoGroupLimitItem,
} from "src/Requests/portfolioGroups";
import { packetErrorHandler } from "src/Utils/requestUtils";

const AdditionalHeader: React.FC = () => (
  <>
    <TableCell />
    <TableCell />
  </>
);

export const getLimitItemAdditional = (
  assetType: number,
): IAdditional<ILimitItemTableReport> => {
  const AdditionalCol: React.FC<{
    row: ILimitItemTableReport;
    onAction: () => void;
  }> = ({ row, onAction }) => {
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [formValues, setFormValues] = useState({
      minValue: row.minValue ?? 0,
      maxValue: row.maxValue ?? 0,
    });
    const onChange = (name: string, value: string | number) => {
      setFormValues((prev) => ({ ...prev, [name]: value }));
    };

    const openEditModal = () => {
      setFormValues({
        minValue: row.minValue ?? 0,
        maxValue: row.maxValue ?? 0,
      });
      setEditModalOpen(true);
    };

    const closeEditModal = () => setEditModalOpen(false);

    const openDeleteModal = () => {
      if (!row.isin) return;
      setDeleteModalOpen(true);
    };

    const closeDeleteModal = () => setDeleteModalOpen(false);

    const submitHandler = () => {
      setEditLoading(true);
      editPortfoGroupLimitItem({
        Id: row.id,
        MinValue: Number(formValues.minValue) || 0,
        MaxValue: Number(formValues.maxValue) || 0,
      })
        .then((resp) => {
          if (!resp.data) toast.error(resp.msg);
          else {
            toast.success(resp.msg);
            onAction();
            closeEditModal();
          }
        })
        .catch(packetErrorHandler)
        .finally(() => setEditLoading(false));
    };

    const deleteSubmitHandler = () => {
      if (!row.isin || row.portfoGroupId == null) return;
      setDeleteLoading(true);
      deletePortfoGroupLimitItem({
        Isin: row.isin,
        assetType,
        PortfoGroupIds: [row.portfoGroupId],
      })
        .then((resp) => {
          toast.success(resp.msg);
          onAction();
          closeDeleteModal();
        })
        .catch(packetErrorHandler)
        .finally(() => setDeleteLoading(false));
    };

    const editModalUI = (
      <div className="flex flex-col justify-start items-start gap-4 min-w-[360px] p-2">
        <TextInput
          name="isin"
          label="نماد"
          extraLabel
          onChange={onChange}
          value={row.instrumentName ?? ""}
          disabled
        />
        <TextInput
          name="minValue"
          label="٪ کف"
          extraLabel
          onChange={onChange}
          value={formValues.minValue.toString()}
        />
        <TextInput
          name="maxValue"
          label="٪ سقف"
          extraLabel
          onChange={onChange}
          value={formValues.maxValue.toString()}
        />
        <Button
          text="ویرایش"
          onClick={submitHandler}
          variant="contained"
          loading={editLoading}
        />
      </div>
    );

    const deleteModalUI = (
      <div className="flex flex-col justify-start items-start gap-4 min-w-[360px] p-2">
        <TextInput
          name="instrumentName"
          label="نماد"
          extraLabel
          onChange={() => undefined}
          value={row.instrumentName ?? ""}
          disabled
        />
        <TextInput
          name="portfoGroupName"
          label="گروه پرتفو"
          extraLabel
          onChange={() => undefined}
          value={row.portfoGroupName ?? ""}
          disabled
        />
        <Button
          text="حذف"
          onClick={deleteSubmitHandler}
          variant="contained"
          loading={deleteLoading}
        />
      </div>
    );

    return row.id ? (
      <>
        <TableCell>
          {editModalOpen && (
            <Modal
              title="ویرایش محدودیت"
              onClose={closeEditModal}
              UI={editModalUI}
              modalBoxClassName="!min-w-min"
            />
          )}
          <div className="w-min mx-auto">
            <div
              className="hover:bg-slate-300 transition-all rounded p-1"
              onClick={openEditModal}
            >
              <EditIcon className="text-sky-700 cursor-pointer" />
            </div>
          </div>
        </TableCell>
        {!row.isDefault && (
          <TableCell>
            {deleteModalOpen && (
              <Modal
                title="حذف محدودیت"
                onClose={closeDeleteModal}
                UI={deleteModalUI}
                modalBoxClassName="!min-w-min"
              />
            )}
            <div
              onClick={deleteLoading ? undefined : openDeleteModal}
              className="hover:bg-red-100 transition-all rounded p-1 w-min mx-auto cursor-pointer"
            >
              {deleteLoading ? (
                <Loading size={16} />
              ) : (
                <DeleteIcon className="text-red-700" fontSize="small" />
              )}
            </div>
          </TableCell>
        )}
      </>
    ) : (
      <>
        <TableCell></TableCell>
        <TableCell></TableCell>
      </>
    );
  };

  return {
    getHeader: AdditionalHeader,
    getRow: AdditionalCol,
  };
};
